import type { ChartLevel, ChartType } from "../config/copy"
import type { PalmLineKey, PalmReading, PalmReadingContent, ThingsKey } from "../types"

const STORAGE_KEY = "palm_reading"

const randomId = () => Math.floor(Math.random() * 19)
const randomPercent = () => Math.floor(Math.random() * (100 - 60)) + 60

/** Build a fresh random reading (line indices + scored life areas), mirroring usePalmReadingData. */
export function generatePalmReading(): PalmReading {
  return {
    lifeLineId: randomId(),
    headLineId: randomId(),
    heartLineId: randomId(),
    specialMarksId: randomId(),
    valueOfLifeTimeId: randomId(),
    relationship: { id: randomId(), percent: randomPercent() },
    success: { id: randomId(), percent: randomPercent() },
    career: { id: randomId(), percent: randomPercent() },
    health: { id: randomId(), percent: randomPercent() },
    lastUpdated: Date.now(),
  }
}

export function getStoredPalmReading(): PalmReading | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as PalmReading) : null
  } catch {
    return null
  }
}

export function persistPalmReading(reading: PalmReading): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reading))
  } catch {
    // ignore quota / privacy-mode failures
  }
}

const fillName = (text: string | undefined, name: string): string =>
  (text ?? "").split("[NAME]").join(name || "User")

const LINE_ID: Record<
  PalmLineKey,
  keyof Pick<PalmReading, "lifeLineId" | "headLineId" | "heartLineId" | "specialMarksId">
> = {
  lifeLine: "lifeLineId",
  headLine: "headLineId",
  heartLine: "heartLineId",
  specialMarks: "specialMarksId",
}

/** Personalised palm-line copy for a section. */
export function getLineText(
  content: PalmReadingContent,
  reading: PalmReading,
  lineKey: PalmLineKey,
  name: string,
): string {
  const id = reading[LINE_ID[lineKey]]
  return fillName(content.palmLines[lineKey]?.[id], name)
}

const THINGS_SLICE: Record<ThingsKey, keyof PalmReadingContent["thingsThatMatter"]> = {
  relationship: "relationship",
  success: "success",
  career: "career",
  health: "heals",
}

/** Personalised "things that matter" summary for a section. */
export function getThingsText(
  content: PalmReadingContent,
  reading: PalmReading,
  thingsKey: ThingsKey,
  name: string,
): string {
  const id = reading[thingsKey].id
  return fillName(content.thingsThatMatter[THINGS_SLICE[thingsKey]]?.[id], name)
}

/** The user's astrology block (falls back to Virgo, as the source does). */
export function getAstrology(content: PalmReadingContent, zodiac: string | undefined) {
  const key = (zodiac ?? "").toLowerCase()
  return content.astrology[key] ?? content.astrology.virgo
}

/** Comma list → trimmed, capitalised labels. */
export function parseZodiacSide(text: string | undefined): string[] {
  if (!text) return []
  return text
    .split(",")
    .map((word) => {
      const trimmed = word.trim()
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    })
    .filter(Boolean)
}

export function chartLevel(value: number): ChartLevel {
  if (value <= 39) return "low"
  if (value <= 79) return "medium"
  return "high"
}

export const CHART_COLORS: Record<ChartType, string> = {
  health: "#50E3C2",
  success: "#FFC014",
  romance: "#FF537F",
  career: "#7FE718",
}
